import { useIsUnityPassActive } from './useIsUnityPassActive'
import { useExperiment } from './useExperiment'
import { UNITYPASS_EXCLUSIVE_SKU } from 'src/constants/experiments'
import { useEffect, useRef, useState } from 'react'
import { useSetAtom } from 'jotai'
import { membershipModalAtom } from 'src/atoms/modals/membershipModal.atom'
import { MEMBERSHIP_MODAL_STEPS } from 'src/modules/Modals/constants'
import { useMembershipWhitelist } from 'src/pages/membership/useMembershipWhitelist'
import { useTracking } from './useTracking'

interface ExclusiveSKUFeatureFlagData {
  title: string
  description: string
  toastMessage: string
  unlockTagText: string
  includedTagText: string
}

interface ExclusiveSKUMainTagsData {
  isExclusiveMembershipProduct: boolean
  unlockTagText: string
  includedTagText: string
}

interface IUseExclusiveSKUMealReturn {
  data: ExclusiveSKUFeatureFlagData | null
  handleClick: (e: React.MouseEvent) => void
  isValidExclusiveSKU: boolean
  isAllowedToAddToCart: boolean
  showExclusiveSkuCTA: boolean
  mainTagsExclusiveSKUData: ExclusiveSKUMainTagsData | null
}

export const useExclusiveSKUMeal = ({
  mealId,
  mealName,
  isExclusiveMembershipProduct = false
}: {
  mealId: number
  mealName: string
  isExclusiveMembershipProduct?: boolean
}): IUseExclusiveSKUMealReturn => {
  const [data, setData] = useState<ExclusiveSKUFeatureFlagData | null>(null)
  const isUnityPassActive = useIsUnityPassActive()
  const isAllowedToSeeMembership = useMembershipWhitelist()
  const setMembership = useSetAtom(membershipModalAtom)
  const hasTrackedExperimentViewed = useRef(false)
  const { track } = useTracking()
  const {
    enabled,
    active: isTreatment,
    payload,
    trackExperimentViewed
  } = useExperiment(UNITYPASS_EXCLUSIVE_SKU)

  useEffect(() => {
    if (enabled && !hasTrackedExperimentViewed.current) {
      trackExperimentViewed({ mealId, mealName })
      hasTrackedExperimentViewed.current = true
    }
  }, [
    enabled,
    hasTrackedExperimentViewed,
    mealId,
    mealName,
    trackExperimentViewed
  ])

  useEffect(() => {
    if (enabled && payload?.value && !data) {
      setData(payload.value)
    }
  }, [payload, enabled, data])

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()

    track('CTA Button Clicked', {
      meal_id: mealId,
      meal_name: mealName,
      experiment_id: UNITYPASS_EXCLUSIVE_SKU.experiment.id,
      experiment_name: UNITYPASS_EXCLUSIVE_SKU.experiment.name
    })

    setMembership(prev => ({
      ...prev,
      isOpen: true,
      step: MEMBERSHIP_MODAL_STEPS.checkout,
      successMessage: data?.toastMessage
    }))
  }

  if (!enabled || !isTreatment || !isAllowedToSeeMembership) {
    return {
      data: null,
      handleClick: () => {},
      isValidExclusiveSKU: false,
      isAllowedToAddToCart: true,
      showExclusiveSkuCTA: false,
      mainTagsExclusiveSKUData: {
        isExclusiveMembershipProduct: false,
        unlockTagText: '',
        includedTagText: ''
      }
    }
  }

  const mainTagsExclusiveSKUData = {
    isExclusiveMembershipProduct,
    unlockTagText: data?.unlockTagText ?? 'Included with UnityPass',
    includedTagText: data?.includedTagText ?? 'Unlock with UnityPass'
  }

  return {
    data,
    handleClick,
    isValidExclusiveSKU: isExclusiveMembershipProduct,
    isAllowedToAddToCart: !isExclusiveMembershipProduct || isUnityPassActive,
    showExclusiveSkuCTA: isExclusiveMembershipProduct && !isUnityPassActive,
    mainTagsExclusiveSKUData
  }
}
